/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/label-has-for */
import React from 'react';
import _ from 'lodash';
import { Control, Errors } from 'react-redux-form';
import './styles.css';
import arrow from '../../../assets/images/select-arrow.svg';

const YearsOfExperienceHeader = 'Years of Experience';
const TransactionsPerYearHeader = 'Transactions per year';

/**
 * The displayable component for the custom investment experience checkbox associated values {years of experience} {transactions per year}
 * @param {*} params The properties to be passed on to the component
 */
const CustomInvestmentDisplayField = ({ display, displayKey }) => (
  <div styleName="col text-right">
    <span styleName="colHeader">{ displayKey === 'transactionsPerYear' ? TransactionsPerYearHeader : YearsOfExperienceHeader}</span>
    <span styleName="colSubHeader">{ display }</span>
  </div>
);

/**
 * The displayable component for the custom investment experience checkbox group associated values {decision making options}
 * @param {*} params The properties to be passed on to the component
 */
const CustomDecisionMakingDisplayField = ({ options, decisionMakingOptions }) =>
  Object.keys(options).filter(key => options[key]).map(key => (<span styleName="colSubHeader singleLine">{ decisionMakingOptions[key] }</span>));

/**
 * The renderer for the years of experience and transactions per year options
 * @param {*} props The properties to be passed on to the component
 */
const renderInvestmentExperienceOption = ({ key, value, displayValue }) => (
  <option key={key} value={value}>{displayValue}</option>
);

/**
 * The renderer for the years of experience and transactions per year select
 * @param {*} props The properties to be passed on to the component
 */
const renderInvestmentExperienceSelect = (props) => {
  let options = [];
  if (Array.isArray(props.options)) {
    options = props.options.map(([key, val]) => renderInvestmentExperienceOption({ key, value: key, displayValue: val }));
  } else {
    options = Object.keys(props.options).map(key => renderInvestmentExperienceOption({ key, value: key, displayValue: props.options[key] }));
  }

  return (
    <div styleName="suitabilityControl2" style={{ display: !props.displayable ? 'none' : 'block' }}>
      <Control.select
        styleName="suitabilityRowSelect2"
        {...props}
      >
        { options }
      </Control.select>
      <img
        styleName="filterArrow2"
        src={arrow}
        alt="arrow"
      />
    </div>
  );
};

/**
 * The edittable component to be rendered on the form's edit mode
 * @param {*} props The properties to be passed on to the edittable component
 */
const CustomInvestmentExperienceEditableField = (props) => {
  switch (props.inputType) {
    case 'select':
      return renderInvestmentExperienceSelect(props);
    default:
      return null;
  }
};

/**
 * The validation handler for the checkbox with the double entendres {years of experience, transactions per year}
 * @param {*} props The properties to be passed on to the component
 */
const validateCheckboxWithDoubleEntendreValidity = ({
  model, key, PLACEHOLDER_OPT,
}) => {
  const hasValueQuerySelector = `[name*='${model}.${key}.hasValue']`;
  const hasValueCheckboxChecked = document.querySelectorAll(hasValueQuerySelector)[0].checked;
  if (hasValueCheckboxChecked) {
    const yearsOfExperienceQuerySelector = `[name*='${model}.${key}.yearsOfExperience']`;
    const transactionsPerYearQuerySelector = `[name*='${model}.${key}.transactionsPerYear']`;
    const yearsOfExperienceValue = document.querySelectorAll(yearsOfExperienceQuerySelector)[0].value;
    const transactionsPerYearValue = document.querySelectorAll(transactionsPerYearQuerySelector)[0].value;
    const yearsOfExperienceInvalid = !yearsOfExperienceValue || !yearsOfExperienceValue.length || yearsOfExperienceValue === PLACEHOLDER_OPT || yearsOfExperienceValue === '-';
    const transactionsPerYearInvalid = !transactionsPerYearValue || !transactionsPerYearValue.length || transactionsPerYearValue === PLACEHOLDER_OPT || transactionsPerYearValue === '-';
    return !yearsOfExperienceInvalid && !transactionsPerYearInvalid;
  }
  return true;
};

/**
 * The on change handler for the checkbox with the double entendres {years of experience, transactions per year}
 * @param {*} props The properties to be passed on to the component
 */
const handleCheckboxWithDoubleEntendreChange = ({
  event, yearOfExperienceModel, transactionaPerYearModel, changeFieldValue,
}) => {
  if (!event.target.checked) {
    event.target.parentNode.parentNode.nextSibling.childNodes[0].attributes.getNamedItem('style').nodeValue = 'display: none;';
    event.target.parentNode.parentNode.nextSibling.childNodes[1].attributes.getNamedItem('style').nodeValue = 'display: none;';
    event.target.parentNode.parentNode.nextSibling.nextSibling.childNodes[0].attributes.getNamedItem('style').nodeValue = 'display: none;';
    event.target.parentNode.parentNode.nextSibling.nextSibling.childNodes[1].attributes.getNamedItem('style').nodeValue = 'display: none;';

    changeFieldValue(yearOfExperienceModel, '');
    changeFieldValue(transactionaPerYearModel, '');
    return;
  }
  event.target.parentNode.parentNode.nextSibling.childNodes[0].attributes.getNamedItem('style').nodeValue = '';
  event.target.parentNode.parentNode.nextSibling.childNodes[1].attributes.getNamedItem('style').nodeValue = '';
  event.target.parentNode.parentNode.nextSibling.nextSibling.childNodes[0].attributes.getNamedItem('style').nodeValue = '';
  event.target.parentNode.parentNode.nextSibling.nextSibling.childNodes[1].attributes.getNamedItem('style').nodeValue = '';
};

/**
 * The factory for the years of experience component properties
 * @param {*} props The properties to be passed on to the object constructor
 */
const getYearsOfExperienceProperties = ({
  model, key, options, yearsOfExperience, onChange,
}) => ({
  model: `${model}.${key}.yearsOfExperience`,
  defaultValue: options[key].yearsOfExperience || '',
  display: yearsOfExperience[options[key].yearsOfExperience],
  label: YearsOfExperienceHeader,
  inputType: 'select',
  options: yearsOfExperience,
  onChange,
});

/**
 * The factory for the transactions per year component properties
 * @param {*} props The properties to be passed on to the object constructor
 */
const getTransactionsPerYearProperties = ({
  model, key, options, transactionsPerYear, onChange,
}) => ({
  model: `${model}.${key}.transactionsPerYear`,
  defaultValue: options[key].transactionsPerYear || '',
  display: transactionsPerYear[options[key].transactionsPerYear],
  label: TransactionsPerYearHeader,
  inputType: 'select',
  options: transactionsPerYear,
  onChange,
});

/**
 * The factory for the associated checkbox for the double entendres {years of experience, transactions per year}
 * @param {*} props The properties to be passed on to the object constructor
 */
const getCheckboxWithDoubleEntendreProperties = ({
  model, key, options, yearOfExperienceModel, transactionaPerYearModel, changeFieldValue, PLACEHOLDER_OPT,
}) => ({
  model: `${model}.${key}.hasValue`,
  defaultValue: options[key].hasValue || false,
  display: options[key].hasValue,
  inputType: 'checkbox',
  onChange: event => handleCheckboxWithDoubleEntendreChange({
    event, yearOfExperienceModel, transactionaPerYearModel, changeFieldValue,
  }),
  errors: {
    required: () => !validateCheckboxWithDoubleEntendreValidity({
      model, key, PLACEHOLDER_OPT,
    }),
  },
  errorHandler: {
    messages: {
      required: 'Please select a value for both dropdowns.',
    },
  },
});

/**
 * The validation handler for the decision makings checkboxes
 * @param {*} props The properties to be passed on to the component
 */
const validateDecisionMakingCheckbox = ({ model, key }) => {
  const decisionMakingOptionsSelector = `[name*='${model}.${key}'`;
  const decisionMakingOptions = document.querySelectorAll(decisionMakingOptionsSelector);
  return Array.from(decisionMakingOptions).filter(comp => comp.checked).length !== 0;
};

/**
 * The factory for a single decision making checkbox
 * @param {*} props The properties to be passed on to the object constructor
 */
const getDecisionMakingCheckboxProperty = ({
  model, key, innerKey, options, changeValidity,
}) => {
  const decisionMakingOptionProperty = {
    model: `${model}.${key}.${innerKey}`,
    defaultValue: options[innerKey] || false,
    display: options[innerKey],
    inputType: 'checkbox',
    innerKey,
  };
  if (innerKey === 'consulting') {
    decisionMakingOptionProperty.errors = {
      required: () => !validateDecisionMakingCheckbox({ model, key }),
    };
    decisionMakingOptionProperty.errorHandler = {
      messages: {
        required: 'Please select at least one option.',
      },
    };
  } else {
    decisionMakingOptionProperty.onChange = () => changeValidity(`${model}.${key}.consulting`, {
      required: () => !validateDecisionMakingCheckbox({ model, key }),
    });
  }
  return decisionMakingOptionProperty;
};

/**
 * The factory for a group of checkboxes belonging to the decision making group
 * @param {*} props The properties to be passed on to the object constructor
 */
const getDecisionMakingProperties = ({
  model, key, options, changeValidity,
}) => Object.keys(options).map(innerKey => getDecisionMakingCheckboxProperty({
  model, key, innerKey, options, changeValidity,
}));

/**
 * The renderer for the decision making checkbox group
 * @param {*} props The properties to be passed on to the component
 */
const renderDecisionMakingCheckboxes = ({
  model, key, options, decisionMakingOptions, changeValidity,
}) => {
  const decisionMakingProperties = getDecisionMakingProperties({
    model, key, options, changeValidity,
  });
  return (
    <div styleName="suitabilityInfo">
      <div styleName="row">
        <div>
          <b>Decision Making</b> <span style={{ fontSize: 14 }}>(check all that apply)</span>
        </div>
      </div>
      <div styleName="row">
        {decisionMakingProperties.map(component => (
          <div styleName="little-margin">
            <label styleName="customControl customControl--checkbox">{decisionMakingOptions[component.innerKey]}
              <Control.checkbox {...component} />
              <div styleName="customControl__indicator" />
            </label>
          </div>
        ))}
      </div>
      <Errors
        styleName="errorText"
        show
        model={decisionMakingProperties[0].model}
        {...decisionMakingProperties[0].errorHandler}
      />
    </div>
  );
};

/**
 * The displayable component for the custom investment experience values
 * @param {*} props The properties to be passed on to the component
 */
export const SuitabilityInvestmentExperienceDisplayField = (props) => {
  const availableKeysForDisplay = Object.keys(props.options).filter(key =>
    Object.keys(props.options[key]).filter(innerkey => (props.options[key][innerkey])).length > 0);
  if (availableKeysForDisplay.length === 0) {
    return (
      <div styleName="suitabilityInfo">
        <div styleName="row">
            N/A
        </div>
      </div>
    );
  }
  return availableKeysForDisplay.map((key) => {
    if (key !== 'decisionMaking') {
      return (
        <div styleName="suitabilityInfo">
          <div styleName="row">
            <div styleName="col wrap">{props.optionLabels[key]}</div>
            <CustomInvestmentDisplayField display={props.yearsOfExperience[props.options[key].yearsOfExperience]} displayKey="yearsOfExperience" />
            <CustomInvestmentDisplayField display={props.transactionsPerYear[props.options[key].transactionsPerYear]} displayKey="transactionsPerYear" />
          </div>
        </div>
      );
    }
    return (
      <div styleName="suitabilityInfo">
        <div styleName="row">
          <div styleName="col wrap">{props.optionLabels[key]}</div>
          <div styleName="col2 text-right">
            <CustomDecisionMakingDisplayField options={props.options[key]} decisionMakingOptions={props.decisionMakingOptions} />
          </div>
        </div>
      </div>
    );
  });
};

/**
 * The edittable wrapper component to be rendered on the form's edit mode
 * @param {*} props The properties to be passed on to the edittable component
 */
export const SuitabilityInvestmentExperienceEditableField = (props) => {
  const checkboxes = Object.keys(props.options).filter(key => !_.isEqual(key, 'decisionMaking')).map((key) => {
    const yearsOfExperienceProps = getYearsOfExperienceProperties({
      model: props.model,
      key,
      options: props.options,
      yearsOfExperience: props.yearsOfExperience,
      onChange: () => props.changeValidity(`${props.model}.${key}.hasValue`, {
        required: () => !validateCheckboxWithDoubleEntendreValidity({
          model: props.model, key, PLACEHOLDER_OPT: props.PLACEHOLDER_OPT,
        }),
      }),
    });

    const transactionsPerYearProps = getTransactionsPerYearProperties({
      model: props.model,
      key,
      options: props.options,
      transactionsPerYear: props.transactionsPerYear,
      onChange: () => props.changeValidity(`${props.model}.${key}.hasValue`, {
        required: () => !validateCheckboxWithDoubleEntendreValidity({
          model: props.model, key, PLACEHOLDER_OPT: props.PLACEHOLDER_OPT,
        }),
      }),
    });

    const checkboxProperties = getCheckboxWithDoubleEntendreProperties({
      model: props.model,
      key,
      options: props.options,
      yearOfExperienceModel: yearsOfExperienceProps.model,
      transactionaPerYearModel: transactionsPerYearProps.model,
      changeFieldValue: props.changeFieldValue,
      PLACEHOLDER_OPT: props.PLACEHOLDER_OPT,
      value: 'true',
    });

    return (
      <div styleName="suitabilityInfo">
        <div styleName="row">
          <div styleName="col wrap">
            <label styleName="customControl customControl--checkbox">{props.optionLabels[key]}
              <Control.checkbox {...checkboxProperties} />
              <div styleName="customControl__indicator" />
            </label>
          </div>
          <div styleName="col text-right">
            <span styleName="colHeader" style={{ display: !props.options[key].hasValue ? 'none' : 'block' }}>{ YearsOfExperienceHeader }</span>
            <CustomInvestmentExperienceEditableField {...yearsOfExperienceProps} displayable={props.options[key].hasValue} />
          </div>
          <div styleName="col text-right">
            <span styleName="colHeader" style={{ display: !props.options[key].hasValue ? 'none' : 'block' }}>{ TransactionsPerYearHeader }</span>
            <CustomInvestmentExperienceEditableField {...transactionsPerYearProps} displayable={props.options[key].hasValue} />
          </div>
        </div>
        <Errors
          styleName="errorText"
          show
          model={checkboxProperties.model}
          {...checkboxProperties.errorHandler}
        />
      </div>
    );
  });
  const decisionMakingCheckboxes = renderDecisionMakingCheckboxes({
    model: props.model,
    key: 'decisionMaking',
    options: props.options.decisionMaking,
    decisionMakingOptions: props.decisionMakingOptions,
    changeValidity: props.changeValidity,
  });
  checkboxes.push(decisionMakingCheckboxes);
  return checkboxes;
};



// WEBPACK FOOTER //
// ./src/components/Forms/Suitability/SuitabilityInvestmentExperience.js